import { DistanceMetric, PlaceDetailsResult } from '@accredible-frontend-v2/services/location';

const EARTH_RADIUS_KM = 6371;

/**
 * Document explaining this formula https://accredible.atlassian.net/wiki/spaces/PD/pages/4311941137/Spotlight+search+for+city+or+country+and+state#Filter-by-city.1
 *
 * Returns radius in kilometers or miles depending on the metric param
 */
export const calculatePlaceRadius = (
  lowLatitude: number,
  lowLongitude: number,
  highLatitude: number,
  highLongitude: number,
  metric: DistanceMetric = DistanceMetric.MILES,
): number => {
  const toRadians = (degrees: number): number => (degrees * Math.PI) / 180;

  const lat1 = toRadians(lowLatitude);
  const lat2 = toRadians(highLatitude);
  const long1 = toRadians(lowLongitude);
  const long2 = toRadians(highLongitude);

  const distanceBetweenTwoPointsInKilometers =
    EARTH_RADIUS_KM *
    Math.acos(
      Math.sin(lat1) * Math.sin(lat2) + Math.cos(lat1) * Math.cos(lat2) * Math.cos(long2 - long1),
    );

  const radiusInKilometers = Math.round(distanceBetweenTwoPointsInKilometers / 2);

  const radiusInMiles = Math.round(radiusInKilometers * 0.62137);

  return metric === DistanceMetric.MILES ? radiusInMiles : radiusInKilometers;
};

export const getRadiusString = (
  result: PlaceDetailsResult,
  currentRadiusString: string,
): string => {
  const {
    geometry: {
      viewport: { southwest, northeast },
    },
  } = result;

  let metric = DistanceMetric.MILES;
  if (currentRadiusString) {
    const [, , metric1] = currentRadiusString?.match(/^(\d+)(mi|km)$/i);
    metric = <DistanceMetric>metric1;
  }

  const radius = calculatePlaceRadius(
    southwest.lat,
    southwest.lng,
    northeast.lat,
    northeast.lng,
    metric,
  );

  return getRadiusInApiFormat(radius, <DistanceMetric>metric);
};

export const getRadiusInApiFormat = (radius: number, metric: DistanceMetric): string => {
  return metric === DistanceMetric.MILES
    ? `${radius}${DistanceMetric.MILES}`
    : `${radius}${DistanceMetric.KILOMETRES}`;
};
